import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  Stars, Sparkles, Heart, Briefcase, Activity, Coins, Loader2, Send,
  Hand, Zap, Calendar, Gem, Users2, MessageCircle, ArrowLeft, X, Upload,
} from "lucide-react";
import { Link, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useSeo } from "@/hooks/useSeo";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import BirthChart3D from "@/components/astrology/BirthChart3D";
import FindPayCheckout from "@/components/FindPayCheckout";
import { useI18n } from "@/context/I18nContext";

const astroErr = (e, fallback) => {
  const d = String(e?.response?.data?.detail || "");
  const msg = /AI_DISABLED|not enabled/i.test(d) ? "This reading needs the AI service, which is currently switched off." : (d && !/^[A-Z_]+:/.test(d) ? d : fallback);
  toast.error(msg, { id: "astro-err", duration: 6000 });
};

const ZODIAC = [
  ["aries","♈","Mar 21 – Apr 19"], ["taurus","♉","Apr 20 – May 20"],
  ["gemini","♊","May 21 – Jun 20"], ["cancer","♋","Jun 21 – Jul 22"],
  ["leo","♌","Jul 23 – Aug 22"], ["virgo","♍","Aug 23 – Sep 22"],
  ["libra","♎","Sep 23 – Oct 22"], ["scorpio","♏","Oct 23 – Nov 21"],
  ["sagittarius","♐","Nov 22 – Dec 21"], ["capricorn","♑","Dec 22 – Jan 19"],
  ["aquarius","♒","Jan 20 – Feb 18"], ["pisces","♓","Feb 19 – Mar 20"],
];

export default function Astrology() {
  const { user } = useAuth();
  const [tab, setTab] = useState(() => new URLSearchParams(window.location.search).get("tab") || "horoscope");
  const { t, lang } = useI18n();
  useSeo({ title: t("astro.seo_title"), description: "AI-powered Vedic + Western astrology: daily horoscope, Kundli birth chart, tarot, palmistry, gemstones, and live consultations." });

  return (
    <div className="min-h-screen bg-gradient-to-b from-indigo-950 via-purple-950 to-stone-950 text-white" data-testid="astrology-page">
      <div className="relative h-52 overflow-hidden">
        <div className="absolute inset-0 opacity-30" style={{ backgroundImage: "radial-gradient(circle at 20% 30%, #fbbf24 0, transparent 40%), radial-gradient(circle at 80% 70%, #a855f7 0, transparent 40%)" }}/>
        <div className="relative max-w-4xl mx-auto px-4 py-6 h-full flex flex-col justify-end">
          <Link to="/apps" className="text-[11px] text-white/70 hover:text-white inline-flex items-center gap-1"><ArrowLeft size={12} className="rtl:rotate-180"/> {t("astro.back")}</Link>
          <div className="text-[11px] uppercase tracking-widest font-black opacity-80 flex items-center gap-1 mt-2"><Stars size={12}/> {t("astro.kicker")}</div>
          <h1 className="heading text-3xl md:text-5xl font-black mt-1">{t("astro.title")}</h1>
          <Link to="/cosmic" className="inline-block mt-2 text-xs font-bold underline text-amber-200" data-testid="astro-cosmic-link">{t("cosmic.title")} · {t("cosmic.numerology")} · {t("cosmic.loshu")} · {t("cosmic.vastu")}</Link>
          <p className="text-sm text-white/70 mt-1 max-w-md">{t("astro.subtitle")}</p>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 py-4">
        {/* Tabs */}
        <div className="flex gap-1.5 flex-wrap">
          {[
            { k: "horoscope",   l: "Horoscope",     I: Sparkles },
            { k: "kundli",      l: "Kundli",        I: Stars    },
            { k: "compat",      l: "Compatibility", I: Heart    },
            { k: "tarot",       l: "Tarot",         I: Zap      },
            { k: "palmistry",   l: "Palmistry AI",  I: Hand     },
            { k: "gemstones",   l: "Gemstones",     I: Gem      },
            { k: "panchang",    l: "Panchang",      I: Calendar },
            { k: "chat",        l: "AI Astrologer", I: MessageCircle },
            { k: "astrologers", l: "Live Astrologers", I: Users2 },
          ].map(({ k, l, I }) => (
            <button key={k} onClick={() => setTab(k)}
              className={`text-xs font-black rounded-full px-3 py-1.5 inline-flex items-center gap-1.5 ${tab === k ? "bg-amber-400 text-stone-900" : "bg-white/10 text-white hover:bg-white/20"}`}
              data-testid={`astro-tab-${k}`}>
              <I size={12}/> {t(`astro.tab.${k}`, l)}
            </button>
          ))}
        </div>

        <div className="mt-4">
          {tab === "horoscope"   && <HoroscopeTab/>}
          {tab === "kundli"      && <KundliTab   user={user}/>}
          {tab === "compat"      && <CompatTab   user={user}/>}
          {tab === "tarot"       && <TarotTab    user={user}/>}
          {tab === "palmistry"   && <PalmistryTab user={user}/>}
          {tab === "gemstones"   && <GemstonesTab/>}
          {tab === "panchang"    && <PanchangTab/>}
          {tab === "chat"        && <ChatTab     user={user}/>}
          {tab === "astrologers" && <AstrologersTab user={user}/>}
        </div>

        {lang !== "en" && <div className="text-[10px] text-amber-200/70 text-center pt-3" data-testid="astro-content-language-note">{t("astro.content_english")}</div>}
        <div className="text-[10px] text-white/40 text-center py-4" lang="en" data-testid="astro-disclaimer">
          For entertainment only. Astrology is a traditional practice, not a substitute for professional medical, legal, or financial advice.
        </div>
      </div>
    </div>
  );
}

// -------- HOROSCOPE ----------------------------------------------------------
function HoroscopeTab() {
  const { t } = useI18n();
  const [sign,   setSign]   = useState("leo");
  const [period, setPeriod] = useState("daily");
  const [data,   setData]   = useState(null);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true); setData(null);
    try {
      const r = await api.get(`/astrology/horoscope`, { params: { sign, period } });
      setData(r.data);
    } catch (e) {
      const d = String(e?.response?.data?.detail || "");
      if (/AI_DISABLED|not enabled/i.test(d)) setData({ unavailable: true });
      else astroErr(e, "Couldn't load the horoscope");
    }
    finally { setLoading(false); }
  }, [sign, period]);
  useEffect(() => { load(); }, [load]);

  if (data?.unavailable) {
    return (
      <div className="rounded-2xl bg-white/5 border border-white/10 p-6 text-center text-sm text-white/70" data-testid="astro-horoscope-unavailable">
        Daily horoscope readings need the AI service, which is switched off (zero-paid-AI mode).
        Try the free <Link to="/cosmic/numerology" className="underline font-bold">Numerology</Link> or <Link to="/cosmic/loshu" className="underline font-bold">Lo Shu Grid</Link> tools.
      </div>
    );
  }
  return (
    <div className="space-y-4">
      <div className="grid grid-cols-3 sm:grid-cols-6 md:grid-cols-12 gap-1.5">
        {ZODIAC.map(([s, sym]) => (
          <button key={s} onClick={() => setSign(s)}
            className={`rounded-2xl p-2 text-center ${sign === s ? "bg-amber-400 text-stone-900" : "bg-white/10 hover:bg-white/20"}`}
            data-testid={`astro-sign-${s}`}>
            <div className="text-2xl">{sym}</div>
            <div className="text-[10px] font-black uppercase mt-0.5">{t(`astro.sign.${s}`, s)}</div>
          </button>
        ))}
      </div>
      <div className="flex gap-1.5">
        {["daily","weekly","monthly"].map(p => (
          <button key={p} onClick={() => setPeriod(p)}
            className={`text-[11px] font-black rounded-full px-3 py-1 ${period === p ? "bg-white text-stone-900" : "bg-white/10 hover:bg-white/20"}`}
            data-testid={`astro-period-${p}`}>
            {t(`astro.period.${p}`, p)}
          </button>
        ))}
      </div>
      {loading ? <div className="p-8 text-center"><Loader2 className="inline animate-spin"/></div> :
       !data ? null :
       <div className="rounded-3xl bg-white/5 backdrop-blur border border-white/10 p-5 space-y-3" data-testid="astro-horoscope-result">
         <p className="text-white/90 leading-relaxed">{data.summary}</p>
         <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
           {[
             { k: "love",    I: Heart,      c: "text-rose-300" },
             { k: "career",  I: Briefcase,  c: "text-blue-300" },
             { k: "health",  I: Activity,   c: "text-emerald-300" },
             { k: "finance", I: Coins,      c: "text-amber-300" },
           ].map(({ k, I, c }) => (
             <div key={k} className="rounded-2xl bg-white/5 p-3">
               <div className={`text-[10px] uppercase tracking-widest font-black ${c} inline-flex items-center gap-1`}><I size={11}/> {t(`astro.area.${k}`, k)}</div>
               <p className="text-[11px] text-white/80 mt-1">{data[k]}</p>
             </div>
           ))}
         </div>
         <div className="flex flex-wrap gap-2 text-[10px]">
           {data.lucky_color   && <span className="rounded-full bg-white/10 px-2 py-0.5">{t("astro.lucky_color")}: {data.lucky_color}</span>}
           {data.lucky_number  && <span className="rounded-full bg-white/10 px-2 py-0.5">{t("astro.lucky_number")}: {data.lucky_number}</span>}
           {data.mood          && <span className="rounded-full bg-white/10 px-2 py-0.5">{t("astro.mood")}: {data.mood}</span>}
           {data.energy_1_to_10 != null && <span className="rounded-full bg-amber-400/20 text-amber-200 px-2 py-0.5">{t("astro.energy")}: {data.energy_1_to_10}/10</span>}
         </div>
       </div>
      }
    </div>
  );
}

// -------- KUNDLI -------------------------------------------------------------
function KundliTab({ user }) {
  const { t } = useI18n();
  const [f, setF] = useState({ name: user?.name || "", dob: "", tob: "12:00", place: "" });
  const [chart, setChart] = useState(null);
  const [busy, setBusy] = useState(false);
  const gen = async () => {
    if (!user) { toast.error(t("astro.sign_in_kundli")); return; }
    if (!f.name || !f.dob || !f.place) { toast.error(t("astro.fill_all")); return; }
    setBusy(true); setChart(null);
    try {
      const r = await api.post(`/astrology/kundli`, f);
      setChart(r.data.chart);
      toast.success(t("astro.kundli_done"));
    } catch (e) { astroErr(e, "Something went wrong. Please try again."); }
    finally { setBusy(false); }
  };
  return (
    <div className="grid md:grid-cols-2 gap-4">
      <div className="rounded-3xl bg-white/5 border border-white/10 p-4 space-y-3">
        <h3 className="font-black">{t("astro.birth_details")}</h3>
        <Input placeholder={t("astro.full_name")} value={f.name} onChange={e => setF({ ...f, name: e.target.value })} className="bg-white/10 border-white/20 rounded-full text-white" data-testid="kundli-name"/>
        <Input type="date" value={f.dob} onChange={e => setF({ ...f, dob: e.target.value })} className="bg-white/10 border-white/20 rounded-full text-white" data-testid="kundli-dob"/>
        <Input type="time" value={f.tob} onChange={e => setF({ ...f, tob: e.target.value })} className="bg-white/10 border-white/20 rounded-full text-white" data-testid="kundli-tob"/>
        <Input placeholder={t("astro.place_of_birth")} value={f.place} onChange={e => setF({ ...f, place: e.target.value })} className="bg-white/10 border-white/20 rounded-full text-white" data-testid="kundli-place"/>
        <Button onClick={gen} disabled={busy} className="w-full rounded-full bg-amber-500 hover:bg-amber-600 text-stone-900 font-black" data-testid="kundli-generate">
          {busy ? <Loader2 size={14} className="animate-spin mr-1"/> : <Stars size={14} className="mr-1"/>} Generate Kundli
        </Button>
      </div>
      <div className="rounded-3xl bg-white/5 border border-white/10 p-4 min-h-[260px]">
        {!chart ? <div className="text-white/50 text-sm text-center py-10">{t("astro.chart_placeholder")}</div> :
        <div className="space-y-3 text-[13px]" data-testid="kundli-result">
          <BirthChart3D rashi={chart.rashi} lagna={chart.lagna} sun={chart.sun_sign} nakshatra={chart.nakshatra}/>
          <div className="grid grid-cols-3 gap-2">
            <Stat label={t("astro.rashi")} value={chart.rashi}/>
            <Stat label={t("astro.nakshatra")} value={chart.nakshatra}/>
            <Stat label={t("astro.lagna")} value={chart.lagna}/>
          </div>
          {chart.summary && <p className="text-white/85 mt-2">{chart.summary}</p>}
          {chart.strengths && (
            <div>
              <div className="text-[10px] uppercase text-emerald-300 font-black mt-2">{t("astro.strengths")}</div>
              <ul className="text-white/80 list-disc list-inside">{chart.strengths.map((s, i) => <li key={i}>{s}</li>)}</ul>
            </div>
          )}
          {chart.remedies && (
            <div>
              <div className="text-[10px] uppercase text-amber-300 font-black mt-2">{t("astro.remedies")}</div>
              <ul className="text-white/80 list-disc list-inside">{chart.remedies.map((s, i) => <li key={i}>{s}</li>)}</ul>
            </div>
          )}
        </div>}
      </div>
    </div>
  );
}

function Stat({ label, value }) {
  return <div className="rounded-2xl bg-white/5 p-2 text-center"><div className="text-[10px] uppercase text-white/60 font-black">{label}</div><div className="font-black text-white text-sm mt-0.5">{value || "—"}</div></div>;
}

// -------- COMPATIBILITY -----------------------------------------------------
function CompatTab({ user }) {
  const [a, setA] = useState({ name: "", dob: "", tob: "12:00", place: "" });
  const [b, setB] = useState({ name: "", dob: "", tob: "12:00", place: "" });
  const [res, setRes] = useState(null);
  const [busy, setBusy] = useState(false);
  const run = async () => {
    if (!user) { toast.error("Sign in"); return; }
    if (!a.name || !a.dob || !a.place || !b.name || !b.dob || !b.place) { toast.error("Fill all fields"); return; }
    setBusy(true); setRes(null);
    try { const r = await api.post("/astrology/compatibility", { a, b }); setRes(r.data.result); }
    catch (e) { astroErr(e, "Something went wrong. Please try again."); }
    finally { setBusy(false); }
  };
  const PersonCard = ({ label, val, set, testid }) => (
    <div className="rounded-3xl bg-white/5 border border-white/10 p-3 space-y-2">
      <h4 className="font-black text-xs uppercase text-white/70">{label}</h4>
      <Input placeholder="Name" value={val.name} onChange={e => set({ ...val, name: e.target.value })} className="bg-white/10 border-white/20 rounded-full text-white text-sm" data-testid={`${testid}-name`}/>
      <Input type="date" value={val.dob} onChange={e => set({ ...val, dob: e.target.value })} className="bg-white/10 border-white/20 rounded-full text-white text-sm" data-testid={`${testid}-dob`}/>
      <Input type="time" value={val.tob} onChange={e => set({ ...val, tob: e.target.value })} className="bg-white/10 border-white/20 rounded-full text-white text-sm"/>
      <Input placeholder="Place" value={val.place} onChange={e => set({ ...val, place: e.target.value })} className="bg-white/10 border-white/20 rounded-full text-white text-sm"/>
    </div>
  );
  return (
    <div className="space-y-3">
      <div className="grid md:grid-cols-2 gap-3">
        <PersonCard label="Person A" val={a} set={setA} testid="compat-a"/>
        <PersonCard label="Person B" val={b} set={setB} testid="compat-b"/>
      </div>
      <Button onClick={run} disabled={busy} className="w-full rounded-full bg-rose-500 hover:bg-rose-600 font-black" data-testid="compat-run">
        {busy ? <Loader2 size={14} className="animate-spin mr-1"/> : <Heart size={14} className="mr-1"/>} Check compatibility
      </Button>
      {res && (
        <div className="rounded-3xl bg-white/5 border border-white/10 p-5 space-y-2" data-testid="compat-result">
          <div className="flex items-center gap-4">
            <div className="text-4xl font-black text-amber-300">{res.overall_percent}%</div>
            <div>
              <div className="text-xs uppercase font-black text-white/70">Verdict</div>
              <div className="font-black text-white capitalize">{res.verdict}</div>
              {res.guna_milan_score != null && <div className="text-[11px] text-white/60">Guna Milan: {res.guna_milan_score}/36</div>}
            </div>
          </div>
          {res.summary && <p className="text-white/85">{res.summary}</p>}
          {res.advice && <ul className="text-[13px] text-white/80 list-disc list-inside">{res.advice.map((s, i) => <li key={i}>{s}</li>)}</ul>}
        </div>
      )}
    </div>
  );
}

// -------- TAROT --------------------------------------------------------------
function TarotTab({ user }) {
  const [question, setQuestion] = useState("");
  const [spread,   setSpread]   = useState("three_card");
  const [result,   setResult]   = useState(null);
  const [busy, setBusy] = useState(false);
  const draw = async () => {
    if (!user) { toast.error("Sign in"); return; }
    if (!question.trim()) { toast.error("Enter your question"); return; }
    setBusy(true); setResult(null);
    try { const r = await api.post("/astrology/tarot", { question, spread }); setResult(r.data); }
    catch (e) { astroErr(e, "Something went wrong. Please try again."); }
    finally { setBusy(false); }
  };
  return (
    <div className="space-y-3">
      <Textarea rows={2} placeholder="What's on your mind?" value={question} onChange={e => setQuestion(e.target.value)} className="bg-white/10 border-white/20 rounded-2xl text-white" data-testid="tarot-question"/>
      <div className="flex gap-2">
        {[
          { k: "three_card",   l: "3 cards" },
          { k: "celtic_cross", l: "Celtic cross (10)" },
        ].map(({ k, l }) => (
          <button key={k} onClick={() => setSpread(k)}
            className={`text-xs font-black rounded-full px-3 py-1.5 ${spread === k ? "bg-amber-400 text-stone-900" : "bg-white/10 hover:bg-white/20"}`}
            data-testid={`tarot-spread-${k}`}>
            {l}
          </button>
        ))}
        <Button onClick={draw} disabled={busy} className="ml-auto rounded-full bg-fuchsia-500 hover:bg-fuchsia-600 font-black" data-testid="tarot-draw">
          {busy ? <Loader2 size={14} className="animate-spin mr-1"/> : <Zap size={14} className="mr-1"/>} Draw
        </Button>
      </div>
      {result && (
        <div className="space-y-2" data-testid="tarot-result">
          <div className="grid grid-cols-2 sm:grid-cols-5 gap-2">
            {result.drawn.map((c, i) => (
              <div key={i} className={`rounded-2xl p-3 border-2 ${c.orientation === "upright" ? "bg-emerald-500/20 border-emerald-400/40" : "bg-rose-500/20 border-rose-400/40"}`}>
                <div className="text-[10px] uppercase font-black text-white/60">{c.position}</div>
                <div className="font-black text-white text-sm">{c.card}</div>
                <div className="text-[10px] text-white/60 italic">{c.orientation}</div>
              </div>
            ))}
          </div>
          {result.reading?.summary && (
            <div className="rounded-3xl bg-white/5 border border-white/10 p-4">
              <p className="text-white/90">{result.reading.summary}</p>
              {result.reading.advice && <p className="text-amber-200 mt-2 text-sm"><b>Advice:</b> {result.reading.advice}</p>}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// -------- PALMISTRY ---------------------------------------------------------
function PalmistryTab({ user }) {
  const [image, setImage] = useState(null);      // {b64, preview}
  const [hand, setHand]   = useState("right");
  const [result, setResult] = useState(null);
  const [followUp, setFollowUp] = useState(null);
  const [busy, setBusy] = useState(false);
  const fileRef = useRef(null);

  const onFile = e => {
    const f = e.target.files?.[0]; if (!f) return;
    if (f.size > 4 * 1024 * 1024) { toast.error("Image too large (max 4MB)"); return; }
    const r = new FileReader();
    r.onload = () => setImage({ b64: r.result.split(",")[1], preview: r.result });
    r.readAsDataURL(f);
  };
  const analyze = async () => {
    if (!user) { toast.error("Sign in"); return; }
    if (!image) { toast.error("Upload a palm photo"); return; }
    setBusy(true); setResult(null); setFollowUp(null);
    try {
      const r = await api.post("/astrology/palmistry", { image_base64: image.b64, dominant_hand: hand });
      setResult(r.data.reading);
      // Fire the follow-up recommendation call (silent — no toast on failure)
      api.get("/astrology/palmistry/follow-up", { params: { limit: 3 } })
         .then(rf => setFollowUp(rf.data))
         .catch(() => {});
    } catch (e) { astroErr(e, "Something went wrong. Please try again."); }
    finally { setBusy(false); }
  };
  return (
    <div className="grid md:grid-cols-2 gap-4">
      <div className="rounded-3xl bg-white/5 border border-white/10 p-4 space-y-3">
        <h3 className="font-black">Upload a clear palm photo</h3>
        <input ref={fileRef} type="file" accept="image/jpeg,image/png,image/webp" onChange={onFile} className="hidden" data-testid="palm-file-input"/>
        <button onClick={() => fileRef.current?.click()} className="w-full rounded-2xl border-2 border-dashed border-white/30 p-6 text-center hover:bg-white/5" data-testid="palm-upload-btn">
          {image?.preview ? (
            <img src={image.preview} alt="palm" className="mx-auto max-h-40 rounded-2xl"/>
          ) : (
            <><Upload size={24} className="mx-auto text-white/50"/><div className="text-xs text-white/60 mt-1">Tap to choose photo</div></>
          )}
        </button>
        <div className="flex gap-2">
          {["left","right"].map(h => (
            <button key={h} onClick={() => setHand(h)} className={`text-xs font-black rounded-full px-3 py-1.5 flex-1 ${hand === h ? "bg-amber-400 text-stone-900" : "bg-white/10 hover:bg-white/20"}`} data-testid={`palm-hand-${h}`}>
              {h} hand
            </button>
          ))}
        </div>
        <Button onClick={analyze} disabled={busy || !image} className="w-full rounded-full bg-amber-500 hover:bg-amber-600 text-stone-900 font-black" data-testid="palm-analyze">
          {busy ? <Loader2 size={14} className="animate-spin mr-1"/> : <Hand size={14} className="mr-1"/>} Read my palm
        </Button>
      </div>
      <div className="rounded-3xl bg-white/5 border border-white/10 p-4 min-h-[260px]">
        {!result ? <div className="text-white/50 text-sm text-center py-10">Upload a photo and tap "Read my palm".</div> :
        <div className="space-y-2 text-[13px]" data-testid="palm-result">
          {result.summary && <p className="text-white/90 mb-2">{result.summary}</p>}
          {["heart_line","head_line","life_line","fate_line","sun_line","personality","love","career","health_note"].map(k => (
            result[k] ? <div key={k}><span className="text-[10px] uppercase text-amber-300 font-black">{k.replace(/_/g," ")}: </span><span className="text-white/85">{result[k]}</span></div> : null
          ))}
          {followUp && followUp.astrologers?.length > 0 && (
            <div className="mt-4 rounded-2xl bg-gradient-to-br from-amber-500/20 to-rose-500/20 border border-amber-400/30 p-3" data-testid="palm-followup-cta">
              <div className="text-[10px] uppercase tracking-widest font-black text-amber-300 flex items-center gap-1"><Hand size={11}/> Want a deeper reading?</div>
              <p className="text-[11px] text-white/85 mt-1">{followUp.reason}</p>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 mt-2">
                {followUp.astrologers.slice(0, 3).map(a => (
                  <div key={a.astrologer_id} className="rounded-xl bg-white/10 p-2 text-[10px]" data-testid={`palm-followup-astro-${a.astrologer_id}`}>
                    <div className="font-black text-white truncate">{a.name}</div>
                    <div className="text-white/60">₹{a.rate_inr_per_min}/min · {a.experience_years}y</div>
                  </div>
                ))}
              </div>
              <button
                onClick={() => document.querySelector("[data-testid='astro-tab-astrologers']")?.click()}
                className="mt-2 w-full rounded-full bg-amber-400 hover:bg-amber-500 text-stone-900 text-xs font-black py-1.5"
                data-testid="palm-followup-book"
              >
                Book a live palmist →
              </button>
            </div>
          )}
        </div>}
      </div>
    </div>
  );
}

// -------- GEMSTONES ---------------------------------------------------------
function GemstonesTab() {
  const [sign, setSign] = useState("leo");
  const [data, setData] = useState(null);
  useEffect(() => {
    const controller = new AbortController();
    api.get(`/astrology/gemstones`, { params: { sign }, signal: controller.signal })
       .then(r => setData(r.data))
       .catch(() => { /* aborted or failed */ });
    return () => controller.abort();
  }, [sign]);
  return (
    <div className="space-y-3">
      <div className="grid grid-cols-4 sm:grid-cols-6 md:grid-cols-12 gap-1.5">
        {ZODIAC.map(([s, sym]) => (
          <button key={s} onClick={() => setSign(s)} className={`rounded-2xl p-2 text-center ${sign === s ? "bg-amber-400 text-stone-900" : "bg-white/10 hover:bg-white/20"}`} data-testid={`gem-sign-${s}`}>
            <div className="text-xl">{sym}</div>
          </button>
        ))}
      </div>
      {data && (
        <div className="rounded-3xl bg-white/5 border border-white/10 p-5" data-testid="gemstone-result">
          <div className="flex items-start gap-3">
            <Gem size={36} className="text-amber-300"/>
            <div>
              <div className="text-[10px] uppercase text-white/60 font-black">Recommended for {data.sign}</div>
              <h3 className="text-2xl font-black">{data.stone}</h3>
              <div className="text-[11px] text-white/70 mt-1">Planet: {data.planet} · Wear: {data.wear}</div>
              <div className="text-[11px] text-white/70 mt-1">Mantra: <em>{data.mantra}</em></div>
              <p className="text-[11px] text-amber-200 mt-3">{data.note}</p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// -------- PANCHANG ----------------------------------------------------------
function PanchangTab() {
  const [data, setData] = useState(null);
  useEffect(() => { api.get(`/astrology/panchang`).then(r => setData(r.data)).catch(() => setData(null)); }, []);
  if (!data) return <Loader2 className="mx-auto animate-spin"/>;
  return (
    <div className="rounded-3xl bg-white/5 border border-white/10 p-5 space-y-3" data-testid="panchang-result">
      <div className="text-xs text-white/60 font-black uppercase">Panchang · {data.date}</div>
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
        <Stat label="Tithi"     value={data.tithi}/>
        <Stat label="Nakshatra" value={data.nakshatra}/>
        <Stat label="Yoga"      value={data.yoga}/>
        <Stat label="Sunrise"   value={data.sunrise}/>
        <Stat label="Sunset"    value={data.sunset}/>
        <Stat label="Abhijit"   value={data.abhijit_muhurat}/>
      </div>
      <div className="text-[11px] text-amber-200">⚠️ Rahu Kalam: {data.rahu_kalam}</div>
      <div>
        <div className="text-[10px] uppercase text-emerald-300 font-black">Auspicious for</div>
        <div className="flex flex-wrap gap-1 mt-1">{data.auspicious_for.map((s, i) => <span key={i} className="text-[10px] bg-emerald-500/20 rounded-full px-2 py-0.5">{s}</span>)}</div>
      </div>
      <div>
        <div className="text-[10px] uppercase text-rose-300 font-black">Avoid</div>
        <div className="flex flex-wrap gap-1 mt-1">{data.avoid.map((s, i) => <span key={i} className="text-[10px] bg-rose-500/20 rounded-full px-2 py-0.5">{s}</span>)}</div>
      </div>
    </div>
  );
}

// -------- CHAT --------------------------------------------------------------
function ChatTab({ user }) {
  const [msgs, setMsgs] = useState([]);
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const [sid, setSid]   = useState(null);
  const listRef = useRef(null);

  const send = async () => {
    if (!user) { toast.error("Sign in"); return; }
    if (!text.trim()) return;
    const t = text.trim();
    setText("");
    setMsgs(m => [...m, { role: "user", text: t }]);
    setBusy(true);
    try {
      const r = await api.post("/astrology/chat", { message: t, session_id: sid });
      setSid(r.data.session_id);
      setMsgs(m => [...m, { role: "assistant", text: r.data.reply }]);
    } catch (e) {
      astroErr(e, "Chat failed. Please try again.");
      setMsgs(m => [...m, { role: "assistant", text: "Sorry, something went wrong. Please try again." }]);
    } finally {
      setBusy(false);
      setTimeout(() => listRef.current?.scrollTo({ top: listRef.current.scrollHeight, behavior: "smooth" }), 100);
    }
  };
  return (
    <div className="rounded-3xl bg-white/5 border border-white/10 p-3 flex flex-col h-[520px]" data-testid="astro-chat">
      <div ref={listRef} className="flex-1 overflow-y-auto space-y-2 pr-1">
        {msgs.length === 0 && <div className="text-white/50 text-sm text-center py-16">Ask anything about your chart, day, transits, remedies…</div>}
        {msgs.map((m, i) => (
          <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`} data-testid={`chat-msg-${m.role}-${i}`}>
            <div className={`max-w-[80%] rounded-2xl p-2.5 text-sm ${m.role === "user" ? "bg-amber-400 text-stone-900" : "bg-white/10 text-white"}`}>{m.text}</div>
          </div>
        ))}
        {busy && <div className="flex justify-start"><div className="bg-white/10 rounded-2xl p-2.5"><Loader2 size={14} className="animate-spin"/></div></div>}
      </div>
      <div className="flex gap-2 pt-2 border-t border-white/10">
        <Input value={text} onChange={e => setText(e.target.value)} onKeyDown={e => e.key === "Enter" && send()} placeholder="Type your question…" className="bg-white/10 border-white/20 rounded-full text-white" data-testid="chat-input"/>
        <Button onClick={send} disabled={busy || !text.trim()} className="rounded-full bg-amber-500 hover:bg-amber-600 text-stone-900 font-black" data-testid="chat-send">
          <Send size={14}/>
        </Button>
      </div>
    </div>
  );
}

// -------- ASTROLOGERS DIRECTORY + BOOKING -----------------------------------
function AstrologersTab({ user }) {
  const [rows, setRows] = useState([]);
  const [book, setBook] = useState(null);
  useEffect(() => { api.get(`/astrology/astrologers`).then(r => setRows(r.data)).catch(() => setRows([])); }, []);
  return (
    <div className="space-y-3">
      {rows.length === 0 ? (
        <div className="rounded-3xl bg-white/5 border border-white/10 p-8 text-center text-sm text-white/60">
          No listed astrologers yet. If you're an astrologer, apply below.
        </div>
      ) : (
        <div className="grid sm:grid-cols-2 gap-3">
          {rows.map(a => (
            <div key={a.astrologer_id} className="rounded-3xl bg-white/5 border border-white/10 p-3 flex gap-3" data-testid={`astrologer-${a.astrologer_id}`}>
              {a.photo_url ? <img src={a.photo_url} alt="" className="w-14 h-14 rounded-2xl object-cover"/>
                          : <div className="w-14 h-14 rounded-2xl bg-amber-400/20 grid place-items-center text-amber-300 font-black">{a.name?.[0]}</div>}
              <div className="flex-1 min-w-0">
                <div className="font-black">{a.name}</div>
                <div className="text-[10px] text-white/60">{a.experience_years}y exp · {a.city || "Any"}</div>
                <div className="text-[10px] text-emerald-300">₹{a.rate_inr_per_min}/min</div>
                {a.specialties?.length > 0 && <div className="flex flex-wrap gap-1 mt-1">{a.specialties.slice(0, 3).map((s, i) => <span key={i} className="text-[9px] bg-white/10 rounded-full px-1.5">{s}</span>)}</div>}
              </div>
              <Button size="sm" onClick={() => setBook(a)} className="rounded-full bg-amber-500 hover:bg-amber-600 text-stone-900 font-black self-start" data-testid={`astrologer-book-${a.astrologer_id}`}>Book</Button>
            </div>
          ))}
        </div>
      )}
      {user && <ApplyAstrologer/>}
      {book && <BookAstrologer astro={book} onClose={() => setBook(null)}/>}
    </div>
  );
}

function ApplyAstrologer() {
  const [open, setOpen] = useState(false);
  const [f, setF] = useState({ name: "", bio: "", rate_inr_per_min: 30, experience_years: 3, city: "", specialties: "", languages: "" });
  const submit = async () => {
    try {
      await api.post("/astrology/astrologers/apply", {
        ...f,
        specialties: f.specialties.split(",").map(x => x.trim()).filter(Boolean),
        languages:   f.languages.split(",").map(x => x.trim()).filter(Boolean),
      });
      toast.success("You're now listed! Refresh the directory.");
      setOpen(false);
    } catch (e) { astroErr(e, "Save failed. Please try again."); }
  };
  return (
    <div className="rounded-3xl bg-white/5 border border-white/10 p-3">
      <button onClick={() => setOpen(o => !o)} className="text-xs font-black text-amber-300 hover:text-amber-200" data-testid="astrologer-apply-toggle">
        {open ? "− Close" : "+ I'm an astrologer — apply to list"}
      </button>
      {open && (
        <div className="mt-3 space-y-2" data-testid="astrologer-apply-form">
          <Input placeholder="Your name" value={f.name} onChange={e => setF({ ...f, name: e.target.value })} className="bg-white/10 border-white/20 rounded-full text-white" data-testid="apply-name"/>
          <Input placeholder="City" value={f.city} onChange={e => setF({ ...f, city: e.target.value })} className="bg-white/10 border-white/20 rounded-full text-white"/>
          <Input placeholder="Specialties (comma)" value={f.specialties} onChange={e => setF({ ...f, specialties: e.target.value })} className="bg-white/10 border-white/20 rounded-full text-white"/>
          <Input placeholder="Languages (comma)" value={f.languages} onChange={e => setF({ ...f, languages: e.target.value })} className="bg-white/10 border-white/20 rounded-full text-white"/>
          <div className="grid grid-cols-2 gap-2">
            <Input type="number" placeholder="₹ per min" value={f.rate_inr_per_min} onChange={e => setF({ ...f, rate_inr_per_min: parseInt(e.target.value) || 30 })} className="bg-white/10 border-white/20 rounded-full text-white"/>
            <Input type="number" placeholder="Years experience" value={f.experience_years} onChange={e => setF({ ...f, experience_years: parseInt(e.target.value) || 0 })} className="bg-white/10 border-white/20 rounded-full text-white"/>
          </div>
          <Textarea rows={3} placeholder="Bio" value={f.bio} onChange={e => setF({ ...f, bio: e.target.value })} className="bg-white/10 border-white/20 rounded-2xl text-white text-sm"/>
          <Button onClick={submit} className="w-full rounded-full bg-amber-500 hover:bg-amber-600 text-stone-900 font-black" data-testid="apply-submit">Apply</Button>
        </div>
      )}
    </div>
  );
}

function BookAstrologer({ astro, onClose }) {
  const [minutes, setMinutes] = useState(15);
  const [channel, setChannel] = useState("chat");
  const [payMode, setPayMode] = useState("findpay");
  const [busy, setBusy] = useState(false);
  const [fpOpen, setFpOpen] = useState(false);
  const nav = useNavigate();
  const total = minutes * astro.rate_inr_per_min;
  const submit = async () => {
    if (payMode === "findpay") { setFpOpen(true); return; }
    setBusy(true);
    try {
      const r = await api.post("/astrology/book", {
        astrologer_id: astro.astrologer_id, minutes, channel,
        origin_url: window.location.origin,
        payment_mode: payMode,
      });
      if (r.data.checkout_url) { window.location.href = r.data.checkout_url; return; }
      if (r.data.room_url || r.data.booking_id) {
        toast.success("Booked · Cash on session — join the room now");
        onClose();
        nav(r.data.room_url || `/astrology/room/${r.data.booking_id}`);
      }
    } catch (e) { astroErr(e, "Booking failed. Please try again."); }
    finally { setBusy(false); }
  };
  const onFindPaySuccess = async (txn) => {
    setFpOpen(false); setBusy(true);
    try {
      const r = await api.post("/astrology/book", {
        astrologer_id: astro.astrologer_id, minutes, channel,
        origin_url: window.location.origin,
        payment_mode: "findpay",
        findpay_txn_id: txn?.txn_id || null,
      });
      toast.success("Session paid · joining room");
      onClose();
      nav(r.data.room_url || `/astrology/room/${r.data.booking_id}`);
    } catch (e) { astroErr(e, "Booking failed after payment — contact support."); }
    finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 bg-black/60 grid place-items-center p-4" style={{ zIndex: "var(--z-modal)" }} onClick={onClose}>
      <div onClick={e => e.stopPropagation()} className="rounded-3xl bg-stone-900 border border-white/10 p-5 w-full max-w-sm space-y-3" data-testid="astrologer-book-modal">
        <div className="flex items-center justify-between">
          <h3 className="font-black text-white">Book · {astro.name}</h3>
          <button onClick={onClose} className="text-white/50 hover:text-white"><X size={16}/></button>
        </div>
        <div className="text-xs text-white/60">₹{astro.rate_inr_per_min}/min</div>
        <div>
          <div className="text-[10px] uppercase font-black text-white/60 mb-1">Duration (minutes)</div>
          <div className="flex gap-1">{[5,15,30,60].map(m => (
            <button key={m} onClick={() => setMinutes(m)} className={`flex-1 rounded-full text-xs font-black px-2 py-1.5 ${minutes === m ? "bg-amber-400 text-stone-900" : "bg-white/10 text-white"}`} data-testid={`book-min-${m}`}>{m}</button>
          ))}</div>
        </div>
        <div>
          <div className="text-[10px] uppercase font-black text-white/60 mb-1">Channel</div>
          <div className="flex gap-1">{["chat","call","video"].map(c => (
            <button key={c} onClick={() => setChannel(c)} className={`flex-1 rounded-full text-xs font-black px-2 py-1.5 ${channel === c ? "bg-amber-400 text-stone-900" : "bg-white/10 text-white"}`} data-testid={`book-ch-${c}`}>{c}</button>
          ))}</div>
        </div>
        <div>
          <div className="text-[10px] uppercase font-black text-white/60 mb-1">Payment</div>
          <div className="flex gap-1">
            <button onClick={() => setPayMode("findpay")} className={`flex-1 rounded-full text-xs font-black px-2 py-1.5 ${payMode === "findpay" ? "bg-emerald-400 text-stone-900" : "bg-white/10 text-white"}`} data-testid="book-pay-findpay">FindPay</button>
            <button onClick={() => setPayMode("stripe")}  className={`flex-1 rounded-full text-xs font-black px-2 py-1.5 ${payMode === "stripe"  ? "bg-amber-400 text-stone-900" : "bg-white/10 text-white"}`} data-testid="book-pay-stripe">Stripe</button>
            <button onClick={() => setPayMode("cod")}     className={`flex-1 rounded-full text-xs font-black px-2 py-1.5 ${payMode === "cod"     ? "bg-amber-400 text-stone-900" : "bg-white/10 text-white"}`} data-testid="book-pay-cod">Cash</button>
          </div>
        </div>
        <Button onClick={submit} disabled={busy} className="w-full rounded-full bg-amber-500 hover:bg-amber-600 text-stone-900 font-black h-10" data-testid="astrologer-book-pay">
          {busy ? <Loader2 size={14} className="animate-spin mr-1"/> : payMode === "cod" ? <>Book &amp; enter room · ₹{total}</> : <>Pay ₹{total} · Book now</>}
        </Button>
      </div>
      {fpOpen && (
        <FindPayCheckout
          open={fpOpen}
          onOpenChange={setFpOpen}
          merchantId="fpm_platform"
          amountPaise={Math.max(1, total) * 100}
          currency="INR"
          description={`Astrology · ${astro.name} · ${minutes}m ${channel}`}
          context={{ kind: "astrology_book", astrologer_id: astro.astrologer_id, minutes, channel }}
          onSuccess={onFindPaySuccess}
        />
      )}
    </div>
  );
}
